import React from 'react';
import { Link } from 'react-router-dom';
import { DataList } from '@/components/common/DataList';
import { Badge } from '@/components/common/Badge';
import { SensitiveValue } from '@/components/common/SensitiveField';
import {
  TransactionContextJson,
  isExplorationContext,
  isAdjustmentContext,
  hasRewardType,
  getRewardTypeLabel,
} from '@/app/types/transactions';

import './TransactionContext.scss';

interface TransactionContextProps {
  context: TransactionContextJson;
  /** Transaction ID for audit logging context */
  transactionId?: string;
}

/**
 * TransactionContext - Human-readable display of transaction context
 * 
 * Renders context fields in a structured format based on transaction type:
 * - EARN: Shows reward type, exploration details, notes
 * - ADJUSTMENT: Shows reason, original transaction link
 * - REDEEM: Shows reward type, notes
 */
export const TransactionContext: React.FC<TransactionContextProps> = ({
  context,
  transactionId,
}) => {
  // Audit context for sensitive field logging
  const auditContext = transactionId ? { transaction_id: transactionId } : undefined;

  // Build items based on context type
  const items: Array<{ label: string; value: React.ReactNode; copyable?: boolean }> = [];

  // Reward Type (if present)
  if (hasRewardType(context)) {
    const rewardTypeLabel = getRewardTypeLabel(context.reward_type);
    items.push({
      label: 'Reward Type',
      value: <Badge variant="default">{rewardTypeLabel}</Badge>,
    });
  }

  // Exploration-specific fields
  if (isExplorationContext(context)) {
    // PHI: Exploration name may reveal health conditions
    items.push({
      label: 'Exploration Name',
      value: (
        <SensitiveValue
          fieldName="experience_name"
          value={context.experience_name}
          resourceType="transaction"
          resourceId={transactionId}
          auditContext={auditContext}
        />
      ),
    });
    items.push({
      label: 'Experience ID',
      value: context.ref_experience_id,
      copyable: true,
    });
    if (context.exploration_signup_id) {
      items.push({
        label: 'Signup ID',
        value: context.exploration_signup_id.toString(),
        copyable: true,
      });
    }
    if ('points' in context && context.points) {
      items.push({
        label: 'Total Exploration Points',
        value: `${context.points} pts`,
      });
    }
  }

  // Original Transaction (for adjustments)
  if (isAdjustmentContext(context) || ('original_transaction_id' in context && context.original_transaction_id)) {
    const originalTxId = 'original_transaction_id' in context ? context.original_transaction_id : null;
    if (originalTxId) {
      items.push({
        label: 'Original Transaction',
        value: (
          <Link 
            to={`/transactions/${originalTxId}`}
            className="transaction-link"
          >
            {truncateUuid(originalTxId)}
          </Link>
        ),
        copyable: false,
      });
    }
  }

  // Transaction Notes - may contain PHI
  if ('transaction_notes' in context && context.transaction_notes) {
    items.push({
      label: 'Notes',
      value: (
        <SensitiveValue
          fieldName="transaction_notes"
          value={context.transaction_notes}
          resourceType="transaction"
          resourceId={transactionId}
          auditContext={auditContext}
        />
      ),
    });
  }

  // Internal Notes - admin-only notes, may contain sensitive information
  if ('internal_notes' in context && context.internal_notes) {
    items.push({
      label: 'Internal Notes',
      value: (
        <SensitiveValue
          fieldName="internal_notes"
          value={context.internal_notes}
          resourceType="transaction"
          resourceId={transactionId}
          auditContext={auditContext}
        />
      ),
    });
  }

  // If no items, show a message
  if (items.length === 0) {
    return (
      <div className="transaction-context transaction-context--empty">
        <span className="empty-message">No additional context available</span>
      </div>
    );
  }

  return (
    <div className="transaction-context">
      <DataList items={items} columns={2} />
    </div>
  );
};

// Helper to truncate UUIDs for display
function truncateUuid(uuid: string): string {
  if (uuid.length <= 12) return uuid;
  return `${uuid.slice(0, 8)}...${uuid.slice(-4)}`;
}

export default TransactionContext;

